// src/hooks/useHumanChat.js
//
// Manages a single conversation: loads history, subscribes to live SSE
// updates, exposes sendMessage(). Separate from useChat.js (the AI
// Ask Previu hook) — no Claude, no tool calls, no widget messages, just
// two humans and a thread.
//
// 18 Jul 2026: added Page Visibility handling — the SSE connection is
// closed while the tab is hidden/backgrounded and reopened when it becomes
// visible again. This is NOT about server load (an idle SSE connection is
// effectively free with virtual threads and doesn't hold a DB connection
// once open-in-view is off) — it's about the user's OWN device: holding a
// persistent connection open while a phone's screen is off or the tab is
// backgrounded has a small but real battery/radio cost on their end, for
// zero benefit since they're not looking at it anyway.

import { useState, useEffect, useCallback, useRef } from 'react';
import { chatMessagingAPI } from '../services/chatMessagingAPI';

const RECONNECT_BASE_DELAY_MS = 2000;
const RECONNECT_MAX_DELAY_MS  = 15000;
// After this many failed reconnect attempts in a row, tell the user live
// updates are paused — before that, retry silently. A single dropped SSE
// connection is normal (network blip, an idle proxy timeout) and sending/
// receiving still works via the plain REST calls underneath; there's no
// reason to alarm the user with "Failed to fetch" over something this
// routine and quick to recover from on its own.
const ATTEMPTS_BEFORE_SHOWING_ERROR = 3;

export function useHumanChat(conversationId) {
  const [messages, setMessages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [sending, setSending]     = useState(false);
  const unsubscribeRef            = useRef(null);
  const reconnectTimeoutRef       = useRef(null);
  const reconnectAttemptsRef      = useRef(0);
  const hiddenRef                 = useRef(false); // true while the tab is backgrounded — suppresses reconnect attempts until visible again

  // Load history + subscribe to live updates whenever conversationId changes.
  useEffect(() => {
    if (!conversationId) {
      // No conversation to load — reset to a clean, NON-loading state.
      //
      // This used to be a bare `return`, which left `loading` on its initial
      // `true` forever, because nothing else ever set it false. Any consumer
      // that renders on chat.loading then showed "Loading conversation…"
      // indefinitely for a conversation that does not exist. PatientCareTeamPage
      // hit exactly that; StaffConversationPage passes conversation?.id and is
      // undefined on first render, so it was exposed too and only escaped
      // because it gates the whole view on its own loading flag.
      //
      // "There is no thread" is a real, settled state, not a pending one.
      setMessages([]);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    reconnectAttemptsRef.current = 0;
    hiddenRef.current = document.visibilityState === 'hidden';

    function loadHistory() {
      return chatMessagingAPI.getMessages(conversationId)
        .then((page) => {
          if (cancelled) return;
          // Page content is newest-first (findByConversationIdOrderByCreatedAtDesc)
          // — reverse for oldest-first chat display.
          const history = [...(page?.content || [])].reverse();
          setMessages(history);
        })
        .catch((e) => { if (!cancelled) setError(e.message || 'Could not load messages.'); });
    }

    setLoading(true);
    setError(null);
    loadHistory().finally(() => { if (!cancelled) setLoading(false); });

    chatMessagingAPI.markRead(conversationId).catch(() => {
      // Non-critical — failing to mark read shouldn't block viewing the thread.
    });

    function disconnect() {
      clearTimeout(reconnectTimeoutRef.current);
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    }

    function connect() {
      disconnect();
      unsubscribeRef.current = chatMessagingAPI.subscribeToConversation(
        conversationId,
        (incoming) => {
          if (cancelled) return;
          // A message just arrived, so the connection is clearly healthy —
          // reset the backoff and clear any "live updates paused" notice.
          reconnectAttemptsRef.current = 0;
          setError(null);
          setMessages(prev => {
            // Guard against a duplicate delivery (e.g. the sender's own optimistic
            // echo arriving again via SSE) — dedupe by id.
            if (prev.some(m => m.id === incoming.id)) return prev;
            return [...prev, incoming];
          });
        },
        () => {
          if (cancelled || hiddenRef.current) return; // tab is backgrounded — don't retry until it's visible again
          reconnectAttemptsRef.current += 1;

          if (reconnectAttemptsRef.current >= ATTEMPTS_BEFORE_SHOWING_ERROR) {
            // Softer than "Failed to fetch" — sending/receiving still work
            // via refresh, only the live push is down, so don't make it
            // sound like the chat itself is broken.
            setError('Live updates paused — refresh to see new messages.');
          }

          const delay = Math.min(
            RECONNECT_BASE_DELAY_MS * reconnectAttemptsRef.current,
            RECONNECT_MAX_DELAY_MS
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            if (!cancelled) connect();
          }, delay);
        }
      );
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        hiddenRef.current = true;
        disconnect();
      } else {
        hiddenRef.current = false;
        reconnectAttemptsRef.current = 0;
        setError(null);
        // Catch up on anything sent while backgrounded — the SSE
        // connection was closed, so any messages from the other party
        // during that window never arrived via push.
        loadHistory();
        chatMessagingAPI.markRead(conversationId).catch(() => {});
        connect();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (!hiddenRef.current) connect();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      disconnect();
    };
  }, [conversationId]);

  const sendMessage = useCallback(async (body, attachmentHealthRecordId) => {
    if (!body?.trim() || !conversationId) return;
    setSending(true);
    try {
      const sent = await chatMessagingAPI.sendMessage(conversationId, body.trim(), attachmentHealthRecordId);
      // Optimistic append — the SSE echo will be deduped by id above when it arrives.
      setMessages(prev => prev.some(m => m.id === sent.id) ? prev : [...prev, sent]);
    } catch (e) {
      setError(e.message || 'Could not send message. Please try again.');
    } finally {
      setSending(false);
    }
  }, [conversationId]);

  return { messages, loading, error, sending, sendMessage };
}
